import React from 'react';
import { useStore, useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useCompareMode, useSelectionMode } from './useCompareMode';
import { useTransferMoney } from './useTransferMoney';
import { useEditHoldings } from './useEditHoldings';
import { useDetailsHoldings } from './useDetailsHoldings';
import { useOtp } from './useOtp';
import { useRiskProfile } from './useRiskProfile';

function useActions() {
  return useStore().actions;
}

function useConnect(selector) {
  return useSelector(selector, shallowEqual);
}

function useCurrency() {
  return { currency: '€' };
}

function useModalOpen(id) {
  return useSelector((state) => state.modals.opened && state.modals.id === id);
}

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = React.useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

function useOnClickOutside(ref, handler, ignoreRef) {
  React.useEffect(() => {
    const listener = (event) => {
      if (
        !ref.current ||
        ref.current.contains(event.target) ||
        ignoreRef?.current?.contains(event.target)
      ) {
        return;
      }

      handler(event);
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler, ignoreRef]);
}

function useModal(id) {
  const { actionModalClose, actionModalOpen } = useActions();
  const isOpen = useSelector((state) => state.modals.opened && state.modals.id === id);
  const modalProps = useSelector((state) =>
    state.modals.id === id ? state.modals.props : undefined
  );

  return { isOpen, close: actionModalClose, open: actionModalOpen, modalProps, props: modalProps };
}

export function useTooltip(id) {
  const { actionTooltipClose, actionTooltipOpen } = useActions();
  const isOpen = useSelector((state) => state.app.tooltipId === id);

  const onChange = React.useCallback(
    (value) => {
      if (value) {
        actionTooltipOpen(id);
      } else {
        actionTooltipClose(id);
      }
    },
    [actionTooltipClose, actionTooltipOpen, id]
  );

  return {
    isOpen,
    close: () => actionTooltipClose(id),
    open: () => actionTooltipOpen(id),
    onChange,
  };
}

function useCopyInput(ref) {
  const [copyStatus, setCopyStatus] = React.useState(null);

  function copy() {
    if (ref?.current) {
      if (navigator.clipboard) {
        navigator.clipboard
          .writeText(ref.current.value)
          .then(() => {
            setCopyStatus('copied');
          })
          .catch(() => {
            setCopyStatus('error');
          });
      } else {
        ref.current.focus();
        ref.current.select();
        setCopyStatus('copied');

        try {
          document.execCommand('copy');
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
          setCopyStatus('error');
        }
      }
    }
    setTimeout(() => {
      setCopyStatus(null);
    }, 5000);
  }

  return { status: copyStatus, copy };
}

function useMedia(queries, values, defaultValue) {
  const mediaQueryLists = queries.map((q) => window.matchMedia(q));

  const getValue = () => {
    const index = mediaQueryLists.findIndex((mql) => mql.matches);
    return typeof values[index] !== 'undefined' ? values[index] : defaultValue;
  };

  const [value, setValue] = React.useState(getValue);

  React.useEffect(
    () => {
      const handler = () => setValue(getValue);
      mediaQueryLists.forEach((mql) => mql.addListener(handler));
      return () => mediaQueryLists.forEach((mql) => mql.removeListener(handler));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return value;
}

const cachedScripts = [];
function useScript(src, options) {
  const [state, setState] = React.useState({
    loaded: false,
    error: false,
  });

  React.useEffect(
    // eslint-disable-next-line consistent-return
    () => {
      if (cachedScripts.includes(src)) {
        setState({
          loaded: true,
          error: false,
        });
      } else {
        cachedScripts.push(src);

        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.defer = options.defer;
        script.id = options.id;

        const onScriptLoad = () => {
          setState({
            loaded: true,
            error: false,
          });
        };

        const onScriptError = () => {
          const index = cachedScripts.indexOf(src);
          if (index >= 0) cachedScripts.splice(index, 1);
          script.remove();

          setState({
            loaded: true,
            error: true,
          });
        };

        script.addEventListener('load', onScriptLoad);
        script.addEventListener('error', onScriptError);

        document.body.appendChild(script);

        return () => {
          script.removeEventListener('load', onScriptLoad);
          script.removeEventListener('error', onScriptError);
        };
      }
    },
    [src, options]
  );

  return [state.loaded, state.error];
}

export {
  useDispatch,
  useSelector,
  useActions,
  useConnect,
  useCompareMode,
  useSelectionMode,
  useTransferMoney,
  useModalOpen,
  useDebounce,
  useOnClickOutside,
  useModal,
  useEditHoldings,
  useDetailsHoldings,
  useCopyInput,
  useMedia,
  useScript,
  useCurrency,
  useRiskProfile,
  useOtp,
};
